// IntradayBidding 入札作成タブ用の集計ロジック (= 全部 frontend mock)。
//
// TODO(business-logic): 本来は domain layer で算出して BFF response に乗せる値。
// Phase 1 では SpotBidding の `models/spotBidValidation.ts` と同様に frontend 純関数で再現する。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { TFunction } from 'i18next'

import type { CoverageTone } from '~/components/bidding/common/coverageColors'
import {
  type CoverageState,
  resolveCoverageState,
  resolveRemainMw,
} from '~/components/bidding/common/coverageColors'
import {
  addToKomaCoverLines,
  coverBidMw,
  createKomaCoverLines,
  readKomaLine,
} from '~/components/bidding/common/positionLineTotals'
import type {
  CoverLine,
  PositionChartSlot,
} from '~/components/bidding/common/types'
import { JEPX_AREA_CODES, convertAreaLabel } from '~/models/area'
import {
  JEPX_PRICE_CONSTRAINT,
  JEPX_VOLUME_CONSTRAINT,
  violatesJepxStep,
} from '~/models/numberInputConstraint'
import { unknownIssuerBidMw } from '~/models/unknownIssuerBid'
import {
  type IntradayBidRow,
  isMarketOrderRow,
  isWithdrawnBidRow,
} from '~/pages/IntradayBidding/models/intradayBidRow'
import {
  type IntradayTableStatus,
  composeIntradayTableStatus,
} from '~/pages/IntradayBidding/models/intradayTableStatus'
import { countCancelledFillsAsPartial } from '~/pages/IntradayBidding/models/marketBoardGapSummary'

// IntradayBidRow.volumeMw / execVolumeMw は read seam (convertIntradayBidsFromOrders.ts)
// で MWh → MW 換算済み。 need (GetProcurementNeed 由来) も MW なので本 module は無換算で扱う。

// ----- KPI 集計 -----

export type IntradayBidKpi = {
  count: number
  buyMw: number
  sellMw: number
  totalMw: number
  /** 約定量 (= exec) 合算 MW。 */
  filledMw: number
  /** 約定率 (= 約定/部分約定 件数 / 全件、 0-1)。 */
  fillRate: number
}

/** 取下 ([[isWithdrawnBidRow]]) を除いた有効札のみ KPI 計算に含める。 */
const isActive = (b: IntradayBidRow): boolean => !isWithdrawnBidRow(b)

export const computeIntradayBidKpi = (
  bids: IntradayBidRow[],
): IntradayBidKpi => {
  let buyMw = 0
  let sellMw = 0
  let filledMw = 0
  let filledCount = 0
  const active = bids.filter(isActive)

  for (const b of active) {
    const vol = Number(b.volumeMw)
    if (b.direction === Side.BUY) buyMw += vol
    else if (b.direction === Side.SELL) sellMw += vol
    if (b.execVolumeMw !== undefined) {
      filledMw += Number(b.execVolumeMw)
      filledCount += 1
    }
  }

  return {
    count: active.length,
    buyMw,
    sellMw,
    totalMw: buyMw + sellMw,
    filledMw,
    fillRate: active.length === 0 ? 0 : filledCount / active.length,
  }
}

// ----- ポジション chart 用 集計 (= 48 コマ × cover / need / remain / coveragePct) -----

const UNKNOWN_ISSUER_COUNTED_STATUSES: ReadonlySet<IntradayTableStatus> =
  new Set(['sending', 'waiting', 'matched', 'partialMatched', 'unmatched'])

/**
 * 一覧のステータス列の区分 (AC-BIN-035) から入札時カバー量の線への写像 (AC-BIN-105)。
 * `Record` なので区分が増えると型エラーで追従漏れを検出できる。 スポットの
 * `coverLineOfSpotCategory` と同じ形。
 *
 * `sending` (送信中、 JEPX の受け付け前。 送れるまで送り直している札を含む) は入札中と同じく
 * 「入札済み」に入れる (2026-10-15 PdM 判断)。 失効・自動取消は取消と同じくどちらの線にも入れない。
 */
const COVER_LINE_OF_INTRADAY_STATUS: Record<IntradayTableStatus, CoverLine> = {
  sending: 'submitted',
  waiting: 'submitted',
  reserved: 'submitted',
  partialMatched: 'submitted',
  matched: 'submitted',
  unmatched: 'submitted',
  draft: 'allOnly',
  pendingApproval: 'allOnly',
  rejected: 'allOnly',
  cancelled: 'excluded',
  autoCancelled: 'excluded',
  expired: 'excluded',
}

export const coverLineOfIntradayStatus = (
  status: IntradayTableStatus,
): CoverLine => COVER_LINE_OF_INTRADAY_STATUS[status]

/**
 * 札一覧から 48 コマの集計を作る (= PositionChart 用)。
 * cover (「全札（下書き含む）」線) = コマあたりの入札量の **net 充当** (= 取消・失効を除く
 *   買 − 売、 AC-BIN-105)。 need と同符号になるので余剰コマ (need<0) に売り札を入れると
 *   need 線に近づく (#2533、 業務正本は mock-app `IntradayBidding.razor.cs`
 *   `BuildGapData`)。 |need| クランプは AC-BIN-012a で廃止した (#4570) ため、
 *   過剰入札・逆方向入札はそのままの値で伝える。 spot 側 `buildPositionSlots` と同じ規則。
 * coverSubmitted (「入札済み」線) = cover の部分集合 (`coverLineOfIntradayStatus` が
 *   'submitted' と判定した札のみ、 AC-BIN-105)。
 * need = backend GetProcurementNeed (market=INTRADAY) 由来の RPC 実データ (needByKoma[i])。
 *   取得エラー時は needByKoma=undefined を渡し、 mock を捏造せず need=0 とする
 *   (設計書 bidding-position-need.md §7 採番、恒久 spec 未反映)。
 * 発注主体区分が空欄の札は cover にも executed にも入れず `unknownIssuerMw` に数える
 *   (AC-BIN-129 / AC-BIN-026a、 #10084)。 約定が cover に入らないまま executed だけに入ると、
 *   板発注画面の自社過不足で必要量が約定で埋まったように見えて入札追加タブと食い違う。
 * 取り下げた札は、 約定の無い取下だけを除き、 取下前に約定した量は部分約定の札として
 *   「入札済み」「全札」の両方と約定量に数える (AC-BIN-105、 空欄の札も同じ。 #8577)。
 */
export const buildIntradayPositionSlots = (
  bids: IntradayBidRow[],
  needByKoma: readonly number[] | undefined,
): PositionChartSlot[] => {
  const lines = createKomaCoverLines()
  const unknownIssuerByKoma = new Map<number, number>()
  // 約定後カバー量 (= 約定量累計)。 約定済 (execVolumeMw あり) の札のみ加算する。
  const execBuyByKoma = new Map<number, number>()
  const execSellByKoma = new Map<number, number>()
  // 取り下げた札のうち取下前に約定した分は、 約定済み量だけの部分約定の札として数える
  // (AC-BIN-105。 約定の無い取下は次の行で除く)。
  for (const b of countCancelledFillsAsPartial(bids)) {
    if (isWithdrawnBidRow(b)) continue
    const isSell = b.direction === Side.SELL
    // 区分と量は札ごとに 1 回だけ決め、 カバー線と区分不明の札の両方へ同じ値を渡す。
    const status = composeIntradayTableStatus(b)
    const coverLine = coverLineOfIntradayStatus(status)
    // 残りがまだ板にあるあいだの部分約定は区分が「入札中」のままなので入札した時点の量。
    const vol = coverBidMw({
      partialMatched: status === 'partialMatched',
      volumeMw: Number(b.volumeMw),
      execVolumeMw:
        b.execVolumeMw === undefined ? undefined : Number(b.execVolumeMw),
    })
    const isUnknownIssuer = b.issuerType === 'unknown'
    if (isUnknownIssuer) {
      const unknownMw = unknownIssuerBidMw({
        counted: UNKNOWN_ISSUER_COUNTED_STATUSES.has(status),
        direction: b.direction,
        mw: vol,
      })
      if (unknownMw !== undefined) {
        unknownIssuerByKoma.set(
          b.timeCode,
          (unknownIssuerByKoma.get(b.timeCode) ?? 0) + unknownMw,
        )
      }
    }
    addToKomaCoverLines(lines, coverLine, {
      komaKey: b.timeCode,
      isSell,
      volume: vol,
      countsToCover: !isUnknownIssuer,
    })
    if (coverLine === 'excluded' || isUnknownIssuer) continue
    if (b.execVolumeMw !== undefined) {
      const execTarget = isSell ? execSellByKoma : execBuyByKoma
      execTarget.set(
        b.timeCode,
        (execTarget.get(b.timeCode) ?? 0) + Number(b.execVolumeMw),
      )
    }
  }
  return Array.from({ length: 48 }, (_, i) => {
    const index = i + 1
    const all = readKomaLine(lines.all, index)
    const submitted = readKomaLine(lines.submitted, index)
    const executed =
      (execBuyByKoma.get(index) ?? 0) - (execSellByKoma.get(index) ?? 0)
    const need = needByKoma?.[i] ?? 0
    return {
      index,
      coverMw: all.coverMw,
      coverSubmittedMw: submitted.coverMw,
      needMw: need,
      // AC-BIN-012a: 入札後残量は 0 で頭打ちにせず、 過剰入札時は負値 (超過量) を表す。
      // 「全札」基準 (= 札作成 modal の prefill が下書きを二重に見積もらないため)。
      // 向きは調達必要量にそろえる (売りのコマでもプラス = 残り / マイナス = 出しすぎ、 #9067)。
      remainMw: resolveRemainMw(all.coverMw, need),
      executedMw: executed,
      unknownIssuerMw: unknownIssuerByKoma.get(index),
      buyMw: all.buyMw,
      sellMw: all.sellMw,
      buySubmittedMw: submitted.buyMw,
      sellSubmittedMw: submitted.sellMw,
      // 入札時カバー率は cover (「全札」基準) ・符号保持・クランプなし (AC-BIN-012a)。
      coveragePct: need !== 0 ? (all.coverMw / need) * 100 : undefined,
    }
  })
}

// ----- 充足率 summary -----

export type IntradayCoverageSummary = {
  /**
   * 全コマ合算 cover の MW (= 各コマ net 充当 買−売 (MW) の単純合算)。
   *
   * 2026-09-08 決定 (AC-BIN-043 改訂) により、 時間前入札は集計値も MWh へ換算せず
   * コマ別数量の単純合算のまま MW で表示する (旧 AC-BIN-043 の MWh 換算 / spot
   * AC-BSP-053 と揃える方針は撤回)。
   */
  totalCoverMw: number
  /** 全コマ合算 need の MW (単純合算)。 */
  totalNeedMw: number
  coverageRate?: number
  /** 状態の語 (= 「買い過剰」「逆方向」等)。 need=0 の placeholder では undefined。 */
  coverageStateKey?: CoverageState['key']
  /** design `js/coverage-state.js` の cls と 1:1 (= {@link resolveCoverageState})。 */
  state: CoverageTone
}

/**
 * 全コマの cover / need を合算して 充足率 summary を返す。
 * 率はクランプせず、 状態の判定は共有の {@link resolveCoverageState} に委ねる (AC-BIN-012a)。
 *
 * 表示用の合計はコマ別数量の単純合算のまま MW で返す (AC-BIN-043、 2026-09-08 決定)。
 * 率は同じ次元同士の除算なので MW のままでも同値 (= spot 側 `summarizeCoverage`
 * が今も MWh 換算するのとは値が変わるが、 率は一致する)。
 */
export const summarizeIntradayCoverage = (
  positions: PositionChartSlot[],
): IntradayCoverageSummary => {
  let totalCoverMw = 0
  let totalNeedMw = 0
  for (const p of positions) {
    totalCoverMw += p.coverMw
    totalNeedMw += p.needMw
  }
  if (totalNeedMw === 0) {
    return { totalCoverMw, totalNeedMw, state: 'neutral' }
  }
  // cover は net 充当 (買−売) で need と同符号のため、 余剰の売り充当では totalCoverMw<0。
  // 率は符号付き同士で除算する (spot 側 summarizeCoverage と同じ理由)。
  const rate = (totalCoverMw / totalNeedMw) * 100
  const coverage = resolveCoverageState(rate, totalNeedMw)
  return {
    totalCoverMw,
    totalNeedMw,
    coverageRate: rate,
    coverageStateKey: coverage.key,
    state: coverage.tone,
  }
}

// ----- エリア別内訳 (= 提出確認モーダル AC-BIN-009 用) -----

/** 提出確認モーダルの「エリア別内訳」1 行 (= spot `SpotAreaBreakdownRow` に受渡契約コードを足した形)。 */
export type IntradayAreaBreakdownRow = {
  areaCode: AreaCode
  areaLabel: string
  count: number
  buyMw: number
  sellMw: number
  /**
   * エリア内の札が持つ受渡契約コード (空でないもの、 重複なし・昇順)。 札が持つ値をそのまま
   * 集め、 区分 × エリアの解決値で補わない (AC-BIN-009)。 同じエリアでも発注主体区分が
   * 違えばコードが分かれるので、 代表 1 件にしない。
   */
  deliveryContractCds: string[]
  /** エリア内にコードを持たない札があるか (コードを持つ札と混ざっても「持たない札がいる」 ことを残す)。 */
  hasBidWithoutDeliveryContractCd: boolean
}

/**
 * 提出確認モーダルの「エリア別内訳」 (AC-BIN-009)。 対象に 1 件も無いエリアは行を
 * 作らない。 並び順は `JEPX_AREA_CODES` (= 北海道 → 九州の系統順) に揃える (spot
 * `buildSpotAreaBreakdown` と同じ理由・同じ並び)。 数量は合計行と同じ MW の単純合算。
 */
export const buildIntradayAreaBreakdown = (
  bids: readonly IntradayBidRow[],
): IntradayAreaBreakdownRow[] => {
  const byArea = new Map<
    AreaCode,
    {
      count: number
      buy: number
      sell: number
      codes: Set<string>
      hasBidWithoutCode: boolean
    }
  >()
  for (const b of bids) {
    const entry = byArea.get(b.areaCode) ?? {
      count: 0,
      buy: 0,
      sell: 0,
      codes: new Set<string>(),
      hasBidWithoutCode: false,
    }
    entry.count += 1
    if (b.deliveryContractCd !== undefined && b.deliveryContractCd !== '') {
      entry.codes.add(b.deliveryContractCd)
    } else {
      entry.hasBidWithoutCode = true
    }
    const v = Number(b.volumeMw)
    if (b.direction === Side.BUY) entry.buy += v
    else if (b.direction === Side.SELL) entry.sell += v
    byArea.set(b.areaCode, entry)
  }
  const out: IntradayAreaBreakdownRow[] = []
  for (const code of JEPX_AREA_CODES) {
    const entry = byArea.get(code)
    if (entry === undefined) continue
    out.push({
      areaCode: code,
      areaLabel: convertAreaLabel(code),
      count: entry.count,
      buyMw: entry.buy,
      sellMw: entry.sell,
      deliveryContractCds: [...entry.codes].sort(),
      hasBidWithoutDeliveryContractCd: entry.hasBidWithoutCode,
    })
  }
  return out
}

/**
 * エリア別内訳の行に併記する受渡契約コードの表示文字列 (AC-BIN-009)。 札が持つコードを昇順で
 * 区切って並べ、 コードを持たない札がいれば「—」 を末尾に足す (持つ札と混ざっても、 持たない札が
 * いることを表示から消さない)。
 */
export const formatAreaDeliveryContractCodes = (
  row: Pick<
    IntradayAreaBreakdownRow,
    'deliveryContractCds' | 'hasBidWithoutDeliveryContractCd'
  >,
  t: TFunction,
): string =>
  [
    ...row.deliveryContractCds,
    ...(row.hasBidWithoutDeliveryContractCd
      ? [t('page:biddingIntraday.submit.deliveryContractCodeNone')]
      : []),
  ].join(t('page:biddingIntraday.submit.deliveryContractCodeSeparator'))

// ----- JEPX 制限違反検出 (= 提出確認モーダルの AC-060/077 違反ブロック用) -----

/**
 * 提出対象の札に JEPX 制度上限 (AC-038、 spot spotBidValidation.ts の
 * `detectLimitViolations` と同一値域) を満たさない札が 1 件でもあるか判定する。
 * 値域は制度値そのもの (`JEPX_VOLUME_CONSTRAINT`: 0.1〜9999.9 MW・0.1 MW 刻み)。
 * 提出の最終確認モーダル (AC-060 = spot AC-077 と同一文言を適用) の違反ブロックに使う。
 */
/**
 * 札 1 件が JEPX 制度値域 (AC-BIN-038) を外れているか。 1 札単位で承認可否を出す
 * 呼び出し元 (時間前の承認確認モーダル、 AC-BIN-097) が一覧の行を組み立てずに
 * 使えるよう、 集計側 [[hasIntradayLimitViolation]] から切り出している。
 *
 * 価格・数量は一覧行の文字列でも RPC 由来の数値でも受ける。 数値化できない値
 * (未取得・空) は「判定材料が無い」 として違反にしない。 値が
 * 取れないことを違反に昇格させると、 詳細取得に失敗しただけで承認できなくなる。
 */
export const violatesIntradayJepxRange = (
  priceYenPerKwh: string | number | undefined,
  volumeMw: string | number | undefined,
): boolean => {
  const price = Number(priceYenPerKwh)
  const volume = Number(volumeMw)
  const priceOk =
    !Number.isFinite(price) ||
    (price >= JEPX_PRICE_CONSTRAINT.min &&
      price <= JEPX_PRICE_CONSTRAINT.max &&
      !violatesJepxStep(price, JEPX_PRICE_CONSTRAINT.step))
  const volumeOk =
    !Number.isFinite(volume) ||
    (volume >= JEPX_VOLUME_CONSTRAINT.min &&
      volume <= JEPX_VOLUME_CONSTRAINT.max &&
      !violatesJepxStep(volume, JEPX_VOLUME_CONSTRAINT.step))
  return !priceOk || !volumeOk
}

/**
 * 一覧の札 1 件が JEPX 制度値域を外れているか。 成行は価格を指定しない
 * (保存値の 0 は価格ではない) ため価格を判定せず数量だけを見る (AC-BIN-004d)。
 */
export const violatesIntradayJepxRangeForRow = (
  bid: Pick<IntradayBidRow, 'priceYenPerKwh' | 'volumeMw' | 'orderMethod'>,
): boolean =>
  violatesIntradayJepxRange(
    isMarketOrderRow(bid) ? undefined : bid.priceYenPerKwh,
    bid.volumeMw,
  )

export const hasIntradayLimitViolation = (bids: IntradayBidRow[]): boolean =>
  bids.some(violatesIntradayJepxRangeForRow)
